"use client";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  File,
  FolderTree,
  Info,
  LoaderCircle,
  LockKeyhole,
  Pencil,
  Plus,
  RotateCcw,
  Settings2,
  Shield,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Trash2,
  Upload,
  Wrench,
  X,
} from "lucide-react";
import {
  type ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  buildRepairedArchive,
  findDeadCodeCandidates,
  findDeadFieldCandidates,
  type ArchiveEdit,
  type DeadCodeMode,
  type RebuildResult,
  type SymbolRewriteProfile,
} from "../../../lib/archive-rebuilder";
import {
  runCompanionDeobfuscation,
} from "../../../lib/local-companion";
import {
  type ScanReport,
  type ScanSession,
} from "../../../lib/types";
import {
  formatBytes,
} from "../../shared/format";
import {
  downloadBlob,
} from "../../shared/io";


export function RepairRebuildView({
  report,
  session,
  file,
  onToast,
}: {
  report: ScanReport;
  session: ScanSession;
  file: File | null;
  onToast: (message: string) => void;
}) {
  const files = useMemo(
    () =>
      report.entries
        .filter((entry) => !entry.directory)
        .sort((left, right) => left.path.localeCompare(right.path)),
    [report.entries],
  );
  const [selectedPath, setSelectedPath] = useState(files[0]?.path ?? "");
  const [profile, setProfile] =
    useState<SymbolRewriteProfile>("readable");
  const [inlineStrings, setInlineStrings] = useState(true);
  const [deadCodeMode, setDeadCodeMode] =
    useState<DeadCodeMode>("safe");
  const [edits, setEdits] = useState<ArchiveEdit[]>([]);
  const [renamePath, setRenamePath] = useState(files[0]?.path ?? "");
  const [addPath, setAddPath] = useState("recovered/new-entry.bin");
  const [textDraft, setTextDraft] = useState("");
  const [textLoading, setTextLoading] = useState(false);
  const [selectedMethodIndex, setSelectedMethodIndex] = useState("");
  const [reason, setReason] = useState("Analyst repair and verified rebuild");
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState<RebuildResult | null>(null);
  const [buildError, setBuildError] = useState("");
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const selectedEntry =
    files.find((entry) => entry.path === selectedPath) ?? null;
  const selectedClass =
    report.classes.find((item) => item.path === selectedPath) ?? null;
  const hasSymbolMap = files.some(
    (entry) => entry.path === "META-INF/jarlab-symbol-map.json",
  );
  const textEditable = Boolean(
    selectedEntry &&
      ["manifest", "config", "text", "script", "document"].includes(
        selectedEntry.kind,
      ) &&
      selectedEntry.size <= 2 * 1024 * 1024,
  );
  const deadCodeCandidates = useMemo(
    () => findDeadCodeCandidates(report, deadCodeMode),
    [deadCodeMode, report],
  );
  const deadFieldCandidates = useMemo(
    () => (deadCodeMode === "none" ? [] : findDeadFieldCandidates(report)),
    [deadCodeMode, report],
  );
  const patchableStringCount = useMemo(
    () =>
      report.recoveredExpressions.filter(
        (item) => item.patchable && item.confidence === "high",
      ).length,
    [report.recoveredExpressions],
  );
  const exactRecoveredStringCount = useMemo(
    () =>
      report.recoveredExpressions.filter(
        (item) => item.confidence === "high",
      ).length,
    [report.recoveredExpressions],
  );
  const plaintextStringLiteralCount = useMemo(
    () =>
      report.classes.reduce(
        (total, classInfo) =>
          total +
          classInfo.methods.reduce(
            (methodTotal, method) =>
              methodTotal +
              method.instructions.filter(
                (instruction) => typeof instruction.literal === "string",
              ).length,
            0,
          ),
        0,
      ),
    [report.classes],
  );

  useEffect(() => {
    if (!textEditable || !selectedPath) return;
    let active = true;
    void Promise.resolve().then(async () => {
      if (!active) return;
      setTextLoading(true);
      try {
        const bytes = await session.readEntry(selectedPath);
        if (active && bytes) {
          setTextDraft(
            new TextDecoder("utf-8", { fatal: false }).decode(bytes),
          );
        }
      } finally {
        if (active) setTextLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [selectedPath, session, textEditable]);

  const queueEdit = (edit: ArchiveEdit) => {
    setEdits((current) => [...current, edit]);
    setResult(null);
    setBuildError("");
  };
  const editId = (type: ArchiveEdit["type"]) =>
    `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const queueRemove = () => {
    if (!selectedPath) return;
    queueEdit({ id: editId("remove"), type: "remove", path: selectedPath });
    onToast("Removal queued");
  };
  const queueRename = () => {
    if (!selectedPath || !renamePath.trim()) return;
    queueEdit({
      id: editId("rename"),
      type: "rename",
      path: selectedPath,
      newPath: renamePath.trim(),
    });
    onToast("Rename queued");
  };
  const queueText = () => {
    if (!selectedPath || !textEditable) return;
    queueEdit({
      id: editId("text"),
      type: "text",
      path: selectedPath,
      text: textDraft,
    });
    onToast("Text replacement queued");
  };
  const queueNeutralize = () => {
    if (!selectedClass || selectedMethodIndex === "") return;
    const methodIndex = Number(selectedMethodIndex);
    const method = selectedClass.methods[methodIndex];
    if (!method || method.name === "<init>") return;
    queueEdit({
      id: editId("neutralize"),
      type: "neutralize",
      path: selectedClass.path,
      methodIndex,
      methodLabel: `${selectedClass.name}.${method.name}${method.descriptor}`,
    });
    onToast("Method neutralization queued");
  };

  const queueReplacement = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedPath) return;
    queueEdit({
      id: editId("replace"),
      type: "replace",
      path: selectedPath,
      bytes: new Uint8Array(await file.arrayBuffer()),
      sourceName: file.name,
    });
    onToast("Binary replacement queued");
  };
  const queueAddition = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !addPath.trim()) return;
    queueEdit({
      id: editId("add"),
      type: "add",
      path: addPath.trim(),
      bytes: new Uint8Array(await file.arrayBuffer()),
      sourceName: file.name,
    });
    onToast("File addition queued");
  };

  const build = async (requestedProfile: SymbolRewriteProfile = profile) => {
    if (building) return;
    setBuilding(true);
    setBuildError("");
    setResult(null);
    try {
      if (
        requestedProfile === "readable" &&
        file &&
        typeof window !== "undefined" &&
        sessionStorage.getItem("jarlab-companion-token-v1")
      ) {
        try {
          const companion = await runCompanionDeobfuscation(file);
          downloadBlob(
            companion.bytes,
            companion.fileName,
            "application/java-archive",
          );
          onToast(`${companion.engine} deobfuscated JAR downloaded`);
          return;
        } catch (companionError) {
          onToast(
            `${
              companionError instanceof Error
                ? companionError.message
                : "Recoverator was unavailable"
            } · using browser fallback`,
          );
        }
      }
      const rebuilt = await buildRepairedArchive(report, session, {
        profile: requestedProfile,
        edits,
        reason,
        inlineRecoveredStrings:
          requestedProfile === "restore" ? false : inlineStrings,
        deadCodeMode:
          requestedProfile === "restore" ? "none" : deadCodeMode,
      });
      setResult(rebuilt);
      if (rebuilt.validation.valid) {
        downloadBlob(
          new Uint8Array(rebuilt.bytes),
          rebuilt.fileName,
          "application/java-archive",
        );
        onToast(`${rebuilt.fileName} downloaded`);
      }
    } catch (error) {
      setBuildError(
        error instanceof Error ? error.message : "The rebuild failed.",
      );
    } finally {
      setBuilding(false);
    }
  };

  const download = () => {
    if (!result?.validation.valid) return;
    downloadBlob(
      new Uint8Array(result.bytes),
      result.fileName,
      "application/java-archive",
    );
    onToast("Validated rebuilt JAR downloaded");
  };

  const profileCopy: Record<
    SymbolRewriteProfile,
    { label: string; detail: string }
  > = {
    none: {
      label: "Repair only",
      detail: "Apply queued file and method edits without renaming symbols.",
    },
    readable: {
      label: "Deobfuscate",
      detail: "Inline proven strings, clean opaque identities, and preserve runtime references.",
    },
    "obfuscate-safe": {
      label: "Safe obfuscation",
      detail: "Rename non-entry-point classes and embed an exact reverse map.",
    },
    "obfuscate-aggressive": {
      label: "Aggressive obfuscation",
      detail: "Rename all eligible classes, including metadata-linked entry points.",
    },
    restore: {
      label: "Restore symbols",
      detail: "Reverse a JarLab symbol map and verify original entry hashes.",
    },
  };

  return (
    <div className="repair-workspace">
      <input
        ref={replaceInputRef}
        className="file-input"
        type="file"
        onChange={(event) => void queueReplacement(event)}
      />
      <input
        ref={addInputRef}
        className="file-input"
        type="file"
        onChange={(event) => void queueAddition(event)}
      />

      <section className="repair-hero">
        <div className="repair-hero-icon">
          <Wrench size={24} />
        </div>
        <div>
          <span>LOCAL BYTECODE SURGERY</span>
          <h2>Deobfuscate and download the JAR</h2>
          <p>
            Recover constant strings, remove verified dead code, rewrite opaque
            symbols, validate every retained class, and download the rebuilt
            archive in one pass.
          </p>
        </div>
        <div className="repair-safety">
          <ShieldCheck size={16} />
          No code execution
        </div>
      </section>

      <section className="quick-deobfuscate">
        <div className="quick-deobfuscate-copy">
          <span>ONE-CLICK CLEANUP</span>
          <h3>Produce a real deobfuscated JAR</h3>
          <p>
            JarLab tries the paired Recoverator engine first, then automatically
            falls back to its verifier-safe browser rewriter. Successful
            validation immediately starts the download.
          </p>
        </div>
        <div className="quick-deobfuscate-stats">
          <div>
            <strong>{exactRecoveredStringCount.toLocaleString()}</strong>
            <span>exactly recovered</span>
          </div>
          <div>
            <strong>{patchableStringCount.toLocaleString()}</strong>
            <span>rewrite candidates</span>
          </div>
          <div>
            <strong>{plaintextStringLiteralCount.toLocaleString()}</strong>
            <span>already plaintext</span>
          </div>
          <div>
            <strong>
              {(
                deadCodeCandidates.length + deadFieldCandidates.length
              ).toLocaleString()}
            </strong>
            <span>dead-code candidates</span>
          </div>
        </div>
        <div className="quick-deobfuscate-options">
          <label>
            <input
              type="checkbox"
              checked={inlineStrings}
              disabled={profile === "restore"}
              onChange={(event) => {
                setInlineStrings(event.target.checked);
                setResult(null);
              }}
            />
            <span>
              <strong>Inline recovered strings</strong>
              <small>
                Only exact, verifier-safe call sites are rewritten
              </small>
            </span>
          </label>
          <label>
            <span>
              <strong>Dead-code removal</strong>
              <small>Previewed before rebuilding</small>
            </span>
            <select
              value={deadCodeMode}
              disabled={profile === "restore"}
              onChange={(event) => {
                setDeadCodeMode(event.target.value as DeadCodeMode);
                setResult(null);
              }}
            >
              <option value="none">Off</option>
              <option value="safe">Safe</option>
              <option value="aggressive">Aggressive</option>
            </select>
          </label>
        </div>
        {deadCodeMode === "aggressive" && (
          <div className="quick-warning">
            <AlertTriangle size={15} />
            Aggressive mode can remove reflectively loaded code. Use it only on
            a copy you can test.
          </div>
        )}
        {buildError && (
          <div className="entry-tool-error compact">
            <AlertTriangle size={15} />
            {buildError}
          </div>
        )}
        {result?.validation.valid && (
          <div className="quick-build-result">
            <Check size={17} />
            <span>
              <strong>{result.fileName}</strong>
              <small>
                {(
                  result.stringRecovery.rewritten +
                  result.stringRecovery.subsumed
                ).toLocaleString()}{" "}
                of{" "}
                {result.stringRecovery.rewriteCandidates} candidate strings
                handled · {result.stringRecovery.rewritten} bytecode replacements
                · {result.stringRecovery.subsumed} nested values folded into
                larger replacements · {result.stringRecovery.skipped} skipped
                safely ·{" "}
                {result.stringRecovery.plaintextLiterals} already plaintext
              </small>
            </span>
            <button type="button" onClick={download}>
              <Download size={14} />
              Download again
            </button>
          </div>
        )}
        <button
          type="button"
          className="primary-button quick-download-button"
          disabled={building}
          onClick={() => void build("readable")}
        >
          {building ? (
            <LoaderCircle size={16} />
          ) : (
            <Download size={16} />
          )}
          {building ? "Deobfuscating & validating…" : "Deobfuscate & download JAR"}
        </button>
      </section>

      <details className="advanced-repair">
        <summary>
          <span>
            <Settings2 size={16} />
            Advanced archive editor
          </span>
          <small>File surgery, method removal, obfuscation and restoration</small>
          <ChevronDown size={16} />
        </summary>
        <div className="repair-grid">
        <section className="repair-panel">
          <div className="repair-panel-head">
            <div>
              <span>1 · WHOLE ARCHIVE</span>
              <h3>Symbol profile</h3>
            </div>
            <Sparkles size={17} />
          </div>
          <div className="profile-options">
            {(
              [
                "none",
                "readable",
                "obfuscate-safe",
                "obfuscate-aggressive",
                "restore",
              ] as SymbolRewriteProfile[]
            ).map((item) => (
              <button
                type="button"
                key={item}
                className={profile === item ? "is-active" : ""}
                disabled={item === "restore" && !hasSymbolMap}
                onClick={() => {
                setProfile(item);
                setResult(null);
                }}
              >
                <span>
                  {item === "restore" ? (
                    <RotateCcw size={15} />
                  ) : item.startsWith("obfuscate") ? (
                    <LockKeyhole size={15} />
                  ) : (
                    <Sparkles size={15} />
                  )}
                </span>
                <strong>{profileCopy[item].label}</strong>
                <small>{profileCopy[item].detail}</small>
              </button>
            ))}
          </div>
        </section>

        <section className="repair-panel target-panel">
          <div className="repair-panel-head">
            <div>
              <span>2 · ENTRY TOOLS</span>
              <h3>Choose a file</h3>
            </div>
            <FolderTree size={17} />
          </div>
          <label className="repair-field">
            <span>Archive entry</span>
            <select
              value={selectedPath}
              onChange={(event) => {
                const path = event.target.value;
                setSelectedPath(path);
                setRenamePath(path);
                setSelectedMethodIndex("");
                setTextDraft("");
              }}
            >
              {files.map((entry) => (
                <option value={entry.path} key={entry.path}>
                  {entry.path}
                </option>
              ))}
            </select>
          </label>
          {selectedEntry && (
            <div className="repair-target-meta">
              <span>{selectedEntry.kind}</span>
              <span>{formatBytes(selectedEntry.size)}</span>
              {selectedClass && (
                <span>{selectedClass.methods.length} methods</span>
              )}
            </div>
          )}

          <div className="repair-tool-row">
            <button type="button" onClick={queueRemove} className="danger-tool">
              <Trash2 size={15} />
              Remove file
            </button>
            <button
              type="button"
              onClick={() => replaceInputRef.current?.click()}
            >
              <Upload size={15} />
              Replace bytes
            </button>
          </div>
          <div className="repair-inline-form">
            <input
              value={renamePath}
              onChange={(event) => setRenamePath(event.target.value)}
              aria-label="New archive path"
            />
            <button type="button" onClick={queueRename}>
              <Pencil size={14} />
              Queue rename
            </button>
          </div>

          {textEditable && (
            <div className="repair-text-editor">
              <div>
                <span>TEXT REPLACEMENT</span>
                <button
                  type="button"
                  onClick={queueText}
                  disabled={textLoading}
                >
                  <Pencil size={13} />
                  Queue text
                </button>
              </div>
              <textarea
                value={textDraft}
                onChange={(event) => setTextDraft(event.target.value)}
                spellCheck={false}
                disabled={textLoading}
                aria-label={`Edit ${selectedPath}`}
              />
            </div>
          )}

          {selectedClass && (
            <div className="method-removal">
              <div>
                <span>METHOD-BODY REMOVAL</span>
                <small>
                  Constructors stay protected; return values are verifier-safe
                  defaults.
                </small>
              </div>
              <select
                value={selectedMethodIndex}
                onChange={(event) => setSelectedMethodIndex(event.target.value)}
              >
                <option value="">Select a method</option>
                {selectedClass.methods.map((method, index) => (
                  <option
                    key={`${method.name}-${method.descriptor}-${index}`}
                    value={index}
                    disabled={method.name === "<init>" || method.codeSize === 0}
                  >
                    {method.name}
                    {method.descriptor}
                    {method.name === "<init>" ? " · protected" : ""}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="danger-tool"
                disabled={selectedMethodIndex === ""}
                onClick={queueNeutralize}
              >
                <ShieldOff size={14} />
                Neutralize selected method
              </button>
            </div>
          )}

          <div className="file-addition">
            <span>ADD NEW FILE</span>
            <div className="repair-inline-form">
              <input
                value={addPath}
                onChange={(event) => setAddPath(event.target.value)}
                aria-label="New file archive path"
              />
              <button
                type="button"
                onClick={() => addInputRef.current?.click()}
              >
                <Plus size={14} />
                Choose file
              </button>
            </div>
          </div>
        </section>

        <section className="repair-panel queue-panel">
          <div className="repair-panel-head">
            <div>
              <span>3 · CHANGESET</span>
              <h3>Queued operations</h3>
            </div>
            <strong className="queue-count">{edits.length}</strong>
          </div>
          <div className="repair-queue">
            {edits.map((edit, index) => (
              <div key={edit.id}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{edit.type.replace("-", " ")}</strong>
                  <code>{edit.path}</code>
                  <small>{editDescriptionForUi(edit)}</small>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${edit.type} operation`}
                  onClick={() => {
                    setEdits((current) =>
                      current.filter((item) => item.id !== edit.id),
                    );
                    setResult(null);
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
            {!edits.length && profile === "none" && (
              <div className="repair-empty-queue">
                <Info size={20} />
                No edits queued. Choose a file tool or a whole-archive profile.
              </div>
            )}
            {!edits.length && profile !== "none" && (
              <div className="repair-empty-queue">
                <Sparkles size={20} />
                {profileCopy[profile].label} will run across the full archive.
              </div>
            )}
          </div>
          <label className="repair-field">
            <span>Audit note</span>
            <input
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="repair-build-actions">
            <button
              type="button"
              className="secondary-button"
              disabled={!edits.length}
              onClick={() => {
                setEdits([]);
                setResult(null);
              }}
            >
              <RotateCcw size={14} />
              Clear queue
            </button>
            <button
              type="button"
              className="primary-button compact"
              disabled={
                building ||
                (!edits.length &&
                  profile === "none" &&
                  !inlineStrings &&
                  deadCodeMode === "none")
              }
              onClick={() => void build(profile)}
            >
              {building ? (
                <LoaderCircle size={14} />
              ) : (
                <Wrench size={14} />
              )}
              {building ? "Rebuilding…" : "Build & download"}
            </button>
          </div>
        </section>

        <section className="repair-panel validation-panel">
          <div className="repair-panel-head">
            <div>
              <span>4 · RELEASE GATE</span>
              <h3>Structural validation</h3>
            </div>
            {result?.validation.valid ? (
              <ShieldCheck size={18} />
            ) : (
              <Shield size={18} />
            )}
          </div>
          {buildError && (
            <div className="entry-tool-error compact">
              <AlertTriangle size={15} />
              {buildError}
            </div>
          )}
          {result ? (
            <>
              <div
                className={`rebuild-verdict ${
                  result.validation.valid ? "is-valid" : "is-invalid"
                }`}
              >
                {result.validation.valid ? (
                  <Check size={20} />
                ) : (
                  <AlertTriangle size={20} />
                )}
                <div>
                  <strong>
                    {result.validation.valid
                      ? "Validated and ready"
                      : "Download blocked"}
                  </strong>
                  <span>
                    {result.validation.entries} entries ·{" "}
                    {result.validation.classesValidated} classes ·{" "}
                    {formatBytes(result.validation.expandedBytes)}
                  </span>
                </div>
              </div>
              <div className="validation-metrics">
                <div>
                  <span>Symbol changes</span>
                  <strong>{result.symbolChanges.toLocaleString()}</strong>
                </div>
                <div>
                  <span>Candidate values handled</span>
                  <strong>
                    {(
                      result.stringRecovery.rewritten +
                      result.stringRecovery.subsumed
                    ).toLocaleString()}{" "}
                    /{" "}
                    {result.stringRecovery.rewriteCandidates.toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span>Bytecode replacements</span>
                  <strong>
                    {result.stringRecovery.rewritten.toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span>Analysis-only values</span>
                  <strong>
                    {result.stringRecovery.analysisOnly.toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span>Dead code removed</span>
                  <strong>
                    {(
                      result.deadClassesRemoved +
                      result.deadFieldsRemoved +
                      result.deadMethodsNeutralized
                    ).toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span>Signatures removed</span>
                  <strong>{result.validation.signaturesRemoved}</strong>
                </div>
                <div>
                  <span>Audit operations</span>
                  <strong>{result.audit.length}</strong>
                </div>
                {result.validation.restorationExpected !== undefined && (
                  <div>
                    <span>Exact entry restoration</span>
                    <strong>
                      {result.validation.restorationMatched}/
                      {result.validation.restorationExpected}
                    </strong>
                  </div>
                )}
              </div>
              {[...result.validation.errors, ...result.validation.warnings].map(
                (item) => (
                  <div className="validation-message" key={item}>
                    <Info size={13} />
                    {item}
                  </div>
                ),
              )}
              <button
                type="button"
                className="primary-button rebuild-download"
                disabled={!result.validation.valid}
                onClick={download}
              >
                <Download size={15} />
                Download {result.fileName}
              </button>
            </>
          ) : (
            <div className="validation-placeholder">
              <ShieldCheck size={28} />
              <strong>No unverified downloads</strong>
              <span>
                JarLab will reopen the generated ZIP, verify paths and CRCs,
                parse every retained class, and resolve the manifest entry
                point before enabling download.
              </span>
            </div>
          )}
        </section>
      </div>
      </details>
    </div>
  );
}


function editDescriptionForUi(edit: ArchiveEdit) {
  if (edit.type === "rename") return `→ ${edit.newPath}`;
  if (edit.type === "text")
    return `${edit.text.length.toLocaleString()} characters`;
  if (edit.type === "replace" || edit.type === "add")
    return `${formatBytes(edit.bytes.length)} · ${edit.sourceName ?? "local bytes"}`;
  if (edit.type === "neutralize") return edit.methodLabel;
  return "Entry will not be included in the rebuilt archive";
}
